import { STYTCH_CLIENT_OPTIONS } from "@pilotfish/core";
import { createStytchUIClient } from "@stytch/react";

/**
 * The one Stytch integration for the control panel — every screen renders under the single
 * `<StytchProvider>` built from this client (src/main.tsx).
 *
 * **There is no sign-in here, and there is not meant to be.** Signing in stays on the login
 * surface — ADR 0010's "one central login page… so #53 onboarding and the future management UI
 * reuse it, not a second integration" is about this app — and this client reads whatever
 * session THIS ORIGIN holds.
 *
 * **That includes a session created on the login host, and it did not until #320.** The SDK
 * writes a host-only cookie unless told otherwise, so a member who signed in at
 * `login[-dev].iamboone.app` arrived here signed out — the opposite of what ADR 0021
 * decision 3 assumes. `STYTCH_CLIENT_OPTIONS` is what makes that assumption true, and it is
 * passed here as well as on the login client because this one writes the cookie too: the SDK
 * refreshes the five-minute JWT in the background, and a client refreshing with different
 * options leaves a second cookie of the same name behind.
 *
 * The client is created from the project's **public token**, which is non-secret by design
 * (it ships in the bundle) and is Terraform-managed — `stytch_public_token` published to
 * `/pilotfish/<env>/my/stytch-public-token` (stytch.tf).
 *
 * **Creating the client is what exercises the SDK's authorized-domain check** (#300). The SDK
 * matches `window.location.origin` against `stytch_consumer_sdk_config.basic.domains`, and an
 * unregistered origin is refused with *"not registered as an allowed domain"* — a #57 field
 * finding, and the reason stytch.tf derives that list from `var.app_host` rather than leaving
 * it to a per-environment tfvars entry somebody could omit.
 */

const publicToken = import.meta.env.VITE_STYTCH_PUBLIC_TOKEN;

const hasToken = typeof publicToken === "string" && publicToken.length > 0;

/**
 * The Stytch UI client, or `null` when the build shipped without a usable public token —
 * either absent, or malformed enough that `createStytchUIClient` rejects it. Null rather than
 * a throw so a misconfigured deploy renders the calm config-error card (main.tsx) instead of
 * white-screening on an exception at module load. The login app's `stytch.ts` makes the same
 * call for the same reason.
 */
export const stytchClient = hasToken ? tryCreateClient(publicToken as string) : null;

function tryCreateClient(token: string) {
  try {
    return createStytchUIClient(token, STYTCH_CLIENT_OPTIONS);
  } catch {
    return null;
  }
}
